import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
    Mic,
    Upload,
    Play,
    Square,
    Trash2,
    Star,
    AlertCircle,
    CheckCircle2,
    Volume2,
    FileAudio,
    Lock,
    Info,
    Loader2,
    RefreshCw,
    X
} from 'lucide-react';
import './VoiceClonePage.css';

const API_BASE = process.env.REACT_APP_API_BASE_URL || 'http://localhost:5001';

// Guided script for voice recording (designed for ~90 seconds when read naturally)
const VOICE_SCRIPT = `Hello! I'm recording my voice to create a personal voice clone. 

Let me start by introducing myself. Today is a great day for creativity, and I'm excited to see what we can build together. The weather outside looks wonderful, doesn't it?

Now, let me try some different expressions. That's amazing! Wait, really? Oh no, I can't believe it! Haha, that's so funny! Hmm, let me think about that for a moment...

Here are some numbers: one, two, three, four, five, six, seven, eight, nine, ten. And the alphabet: A, B, C, D, E, F, G, H, I, J, K, L, M, N, O, P, Q, R, S, T, U, V, W, X, Y, Z.

Questions are important too. How are you doing today? What do you think about this? Can you believe how fast technology is advancing?

Let me read some varied content: The quick brown fox jumps over the lazy dog. Pack my box with five dozen liquor jugs. She sells seashells by the seashore.

And finally, some closing thoughts. Thank you so much for listening! I hope this recording captures my voice well. Have a fantastic day, and remember to always stay creative!`;

export function VoiceClonePage({ session, refreshUsage }) {
    // State
    const [voices, setVoices] = useState([]);
    const [voiceStatus, setVoiceStatus] = useState({ current: 0, limit: 0, allowed: true, tier: 'free' });
    const [loading, setLoading] = useState(true);
    const [cloning, setCloning] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');

    // Form state
    const [voiceName, setVoiceName] = useState('');
    const [audioFile, setAudioFile] = useState(null);
    const [consent, setConsent] = useState(false);
    const [dragActive, setDragActive] = useState(false);

    // Recording state
    const [isRecording, setIsRecording] = useState(false);
    const [recordingTime, setRecordingTime] = useState(0);
    const [recordedBlob, setRecordedBlob] = useState(null);
    const [recordingError, setRecordingError] = useState('');
    const [audioLevel, setAudioLevel] = useState(0);

    // Testing state
    const [testingVoiceId, setTestingVoiceId] = useState(null);

    const mediaRecorderRef = useRef(null);
    const audioChunksRef = useRef([]);
    const timerRef = useRef(null);
    const analyserRef = useRef(null);
    const animationRef = useRef(null);
    const streamRef = useRef(null);

    // Fetch voices
    const fetchVoices = useCallback(async () => {
        if (!session?.access_token) return;

        try {
            const res = await fetch(`${API_BASE}/api/voice-clone/voices`, {
                headers: { Authorization: `Bearer ${session.access_token}` }
            });
            const data = await res.json();
            if (res.ok) {
                setVoices(data.voices || []);
                setVoiceStatus({
                    current: data.current_count || 0,
                    limit: data.limit || 0,
                    allowed: data.can_clone || false,
                    tier: data.tier || 'free'
                });
            }
        } catch (err) {
            console.error('Failed to fetch voices:', err);
        } finally {
            setLoading(false);
        }
    }, [session]);

    useEffect(() => {
        fetchVoices();
    }, [fetchVoices]);

    // Cleanup on unmount
    useEffect(() => {
        return () => {
            if (timerRef.current) clearInterval(timerRef.current);
            if (animationRef.current) cancelAnimationFrame(animationRef.current);
            if (streamRef.current) {
                streamRef.current.getTracks().forEach(track => track.stop());
            }
        };
    }, []);

    // Start recording
    const startRecording = async () => {
        setRecordingError('');
        setRecordedBlob(null);
        setAudioFile(null);
        audioChunksRef.current = [];

        try {
            const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
            streamRef.current = stream;

            // Setup audio analyser for level visualization
            const audioContext = new (window.AudioContext || window.webkitAudioContext)();
            const source = audioContext.createMediaStreamSource(stream);
            const analyser = audioContext.createAnalyser();
            analyser.fftSize = 256;
            source.connect(analyser);
            analyserRef.current = analyser;

            // Animate audio level
            const updateLevel = () => {
                if (!analyserRef.current) return;
                const dataArray = new Uint8Array(analyserRef.current.frequencyBinCount);
                analyserRef.current.getByteFrequencyData(dataArray);
                const average = dataArray.reduce((a, b) => a + b, 0) / dataArray.length;
                setAudioLevel(average / 255);
                animationRef.current = requestAnimationFrame(updateLevel);
            };
            updateLevel();

            // Setup MediaRecorder
            const mediaRecorder = new MediaRecorder(stream, { mimeType: 'audio/webm' });
            mediaRecorderRef.current = mediaRecorder;

            mediaRecorder.ondataavailable = (e) => {
                if (e.data.size > 0) {
                    audioChunksRef.current.push(e.data);
                }
            };

            mediaRecorder.onstop = () => {
                const blob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
                setRecordedBlob(blob);

                // Convert to file for upload
                const file = new File([blob], 'voice-recording.webm', { type: 'audio/webm' });
                setAudioFile(file);

                // Cleanup
                if (animationRef.current) cancelAnimationFrame(animationRef.current);
                setAudioLevel(0);
                stream.getTracks().forEach(track => track.stop());
            };

            mediaRecorder.start(100); // Collect data every 100ms
            setIsRecording(true);
            setRecordingTime(0);

            // Start timer
            timerRef.current = setInterval(() => {
                setRecordingTime(prev => prev + 1);
            }, 1000);

        } catch (err) {
            console.error('Recording error:', err);
            if (err.name === 'NotAllowedError') {
                setRecordingError('Microphone access denied. Please allow microphone access in your browser settings.');
            } else if (err.name === 'NotFoundError') {
                setRecordingError('No microphone found. Please connect a microphone and try again.');
            } else {
                setRecordingError('Failed to start recording. Please try again.');
            }
        }
    };

    // Stop recording
    const stopRecording = () => {
        if (mediaRecorderRef.current && isRecording) {
            mediaRecorderRef.current.stop();
            setIsRecording(false);
            if (timerRef.current) {
                clearInterval(timerRef.current);
                timerRef.current = null;
            }
        }
    };

    // Clear recording
    const clearRecording = () => {
        setRecordedBlob(null);
        setAudioFile(null);
        setRecordingTime(0);
    };

    // Format time as MM:SS
    const formatTime = (seconds) => {
        const mins = Math.floor(seconds / 60);
        const secs = seconds % 60;
        return `${mins}:${secs.toString().padStart(2, '0')}`;
    };

    // Handle file drop
    const handleDrag = (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (e.type === 'dragenter' || e.type === 'dragover') {
            setDragActive(true);
        } else if (e.type === 'dragleave') {
            setDragActive(false);
        }
    };

    const handleDrop = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setDragActive(false);

        if (e.dataTransfer.files && e.dataTransfer.files[0]) {
            const file = e.dataTransfer.files[0];
            if (validateAudioFile(file)) {
                setAudioFile(file);
                setRecordedBlob(null); // Clear any recording
            }
        }
    };

    const validateAudioFile = (file) => {
        const allowedTypes = ['audio/mpeg', 'audio/wav', 'audio/mp4', 'audio/ogg', 'audio/webm', 'audio/flac', 'audio/x-m4a'];
        if (!allowedTypes.includes(file.type)) {
            setError('Invalid file type. Please upload an MP3, WAV, M4A, OGG, or FLAC file.');
            return false;
        }
        if (file.size > 50 * 1024 * 1024) {
            setError('File too large. Maximum size is 50MB.');
            return false;
        }
        return true;
    };

    const handleFileChange = (e) => {
        const file = e.target.files[0];
        if (file && validateAudioFile(file)) {
            setAudioFile(file);
            setRecordedBlob(null); // Clear any recording
            setError('');
        }
    };

    // Clone voice
    const handleClone = async (e) => {
        e.preventDefault();
        setError('');
        setSuccess('');

        if (!voiceName.trim()) {
            setError('Please enter a name for your voice.');
            return;
        }
        if (!audioFile) {
            setError('Please record or upload an audio file.');
            return;
        }
        if (!consent) {
            setError('Please confirm you have the right to clone this voice.');
            return;
        }
        if (recordedBlob && recordingTime < 30) {
            setError('Recording is too short. Please record at least 30 seconds for best results.');
            return;
        }

        setCloning(true);

        try {
            const formData = new FormData();
            formData.append('name', voiceName.trim());
            formData.append('audio', audioFile);
            formData.append('consent', 'true');

            const res = await fetch(`${API_BASE}/api/voice-clone/clone`, {
                method: 'POST',
                headers: { Authorization: `Bearer ${session.access_token}` },
                body: formData
            });

            const data = await res.json();

            if (!res.ok) {
                throw new Error(data.error || 'Failed to clone voice');
            }

            setSuccess(`Voice "${voiceName}" cloned successfully! ${data.credits_used} credits used.`);
            setVoiceName('');
            setAudioFile(null);
            setRecordedBlob(null);
            setRecordingTime(0);
            setConsent(false);
            fetchVoices();
            if (refreshUsage) refreshUsage();
        } catch (err) {
            setError(err.message);
        } finally {
            setCloning(false);
        }
    };

    // Delete voice
    const handleDelete = async (voiceId, voiceName) => {
        if (!window.confirm(`Are you sure you want to delete "${voiceName}"? This cannot be undone.`)) {
            return;
        }

        try {
            const res = await fetch(`${API_BASE}/api/voice-clone/voices/${voiceId}`, {
                method: 'DELETE',
                headers: { Authorization: `Bearer ${session.access_token}` }
            });

            if (!res.ok) {
                const data = await res.json();
                throw new Error(data.error || 'Failed to delete voice');
            }

            setSuccess(`Voice "${voiceName}" deleted.`);
            fetchVoices();
        } catch (err) {
            setError(err.message);
        }
    };

    // Set default voice
    const handleSetDefault = async (voiceId, isCurrentlyDefault) => {
        try {
            if (isCurrentlyDefault) {
                await fetch(`${API_BASE}/api/voice-clone/voices/${voiceId}/default`, {
                    method: 'DELETE',
                    headers: { Authorization: `Bearer ${session.access_token}` }
                });
            } else {
                await fetch(`${API_BASE}/api/voice-clone/voices/${voiceId}/default`, {
                    method: 'POST',
                    headers: { Authorization: `Bearer ${session.access_token}` }
                });
            }
            fetchVoices();
        } catch (err) {
            setError('Failed to update default voice');
        }
    };

    // Test voice
    const handleTestVoice = async (voice) => {
        if (testingVoiceId) return;
        setTestingVoiceId(voice.id);

        try {
            const voiceSessionId = crypto.randomUUID ? crypto.randomUUID() :
                `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

            const res = await fetch(`${API_BASE}/api/rant/generate-voice`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${session.access_token}`
                },
                body: JSON.stringify({
                    sentences: [{
                        text: "This is a preview of my cloned voice.",
                        voice: voice.elevenlabs_voice_id
                    }],
                    voice_session_id: voiceSessionId
                })
            });

            if (!res.ok) {
                const data = await res.json();
                throw new Error(data.error || 'Failed to generate preview');
            }

            const data = await res.json();
            if (data.job_id) {
                const audioUrl = `${API_BASE}/api/rant/audio/${data.job_id}`;
                const audio = new Audio(audioUrl);
                audio.play().catch(e => console.error("Play failed:", e));
            }

        } catch (err) {
            setError(`Test failed: ${err.message}`);
        } finally {
            setTestingVoiceId(null);
        }
    };

    // Check if user can clone
    const canClone = voiceStatus.limit > 0 && voiceStatus.current < voiceStatus.limit;
    const needsUpgrade = voiceStatus.limit === 0;
    const hasAudio = audioFile !== null;
    const isRecordingReady = recordedBlob !== null && recordingTime >= 30;

    if (loading) {
        return (
            <div className="voice-clone-page">
                <div className="voice-clone-loading">
                    <Loader2 size={40} className="loading-spinner" />
                    <p>Loading Voice Lab...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="voice-page-wrapper">
            {/* Header */}
            {/* Header */}
            <div className="voice-clone-header-container">
                <h1 className="voice-clone-title">
                    Voice Lab
                </h1>
                <p className="voice-clone-subtitle">
                    Clone your voice and use it across all CutIQ tools
                </p>
            </div>

            <div className="voice-dashboard-container">

                {/* Tier Gate */}
                {needsUpgrade && (
                    <div className="voice-clone-upgrade">
                        <Lock size={48} className="upgrade-icon" />
                        <h2>Pro Feature</h2>
                        <p>Voice cloning is available for Pro and Studio subscribers.</p>
                        <p className="upgrade-tiers">
                            <strong>Pro:</strong> 1 cloned voice &nbsp;|&nbsp; <strong>Studio:</strong> 3 cloned voices
                        </p>
                        <a href="/settings" className="upgrade-btn">Upgrade Now</a>
                    </div>
                )}

                {/* Messages */}
                {error && <div className="voice-clone-message error"><AlertCircle size={16} /> {error}</div>}
                {success && <div className="voice-clone-message success"><CheckCircle2 size={16} /> {success}</div>}
                {recordingError && <div className="voice-clone-message error"><AlertCircle size={16} /> {recordingError}</div>}

                {/* Main Content Grid */}
                <div className="voice-content-grid">
                    {/* Left Column */}
                    <div className="voice-creator-column">
                        {/* Clone Form */}
                        {!needsUpgrade && (
                            <form className="voice-clone-form" onSubmit={handleClone}>
                                <div className="form-section">
                                    <h2>Clone Your Voice</h2>
                                </div>



                                {/* RECORD Section */}
                                <div className="audio-input-section">
                                    <div className="section-header">
                                        <Mic size={20} className="section-icon" />
                                        <h3>Record Your Voice</h3>
                                    </div>

                                    {/* Script to read */}
                                    <div className="script-container">
                                        <div className="script-header">
                                            <Info size={16} />
                                            <span>Read this script aloud (takes ~90 seconds)</span>
                                        </div>
                                        <div className="script-content">
                                            {VOICE_SCRIPT}
                                        </div>
                                    </div>

                                    {/* Recording Controls */}
                                    <div className="recording-controls">
                                        {!isRecording && !recordedBlob && (
                                            <div className="record-btn-container">
                                                <button
                                                    type="button"
                                                    className="record-btn standard-btn"
                                                    onClick={startRecording}
                                                    disabled={!canClone || cloning}
                                                    title="Start Recording"
                                                >
                                                    <Mic size={18} />
                                                    <span>Tap to Record</span>
                                                </button>
                                            </div>
                                        )}

                                        {isRecording && (
                                            <div className="recording-active">
                                                <div className="recording-indicator">
                                                    <span className="pulse-dot"></span>
                                                    <span className="recording-time">{formatTime(recordingTime)}</span>
                                                    <div
                                                        className="audio-level-bar"
                                                        style={{ '--level': audioLevel }}
                                                    ></div>
                                                </div>
                                                <div className="recording-hint">
                                                    {recordingTime < 30 && <span className="time-warning">Record at least 30 seconds</span>}
                                                    {recordingTime >= 30 && recordingTime < 60 && <span className="time-ok">Good! Keep going for better quality</span>}
                                                    {recordingTime >= 60 && <span className="time-great">Great! You have enough audio</span>}
                                                </div>
                                                <button
                                                    type="button"
                                                    className="stop-btn"
                                                    onClick={stopRecording}
                                                >
                                                    <Square size={20} fill="currentColor" />
                                                    Stop Recording
                                                </button>
                                            </div>
                                        )}

                                        {recordedBlob && !isRecording && (
                                            <div className="recording-preview">
                                                <div className="preview-info">
                                                    <FileAudio size={20} className="preview-icon" />
                                                    <span>Recording: {formatTime(recordingTime)}</span>
                                                    {recordingTime < 30 && <span className="preview-warning">(Too short - re-record for best results)</span>}
                                                </div>
                                                <audio controls src={URL.createObjectURL(recordedBlob)} className="audio-preview" />
                                                <button
                                                    type="button"
                                                    className="re-record-btn"
                                                    onClick={clearRecording}
                                                >
                                                    <RefreshCw size={14} style={{ marginRight: '6px' }} /> Re-record
                                                </button>
                                            </div>
                                        )}
                                    </div>
                                </div>

                                {/* OR Divider */}
                                <div className="or-divider">
                                    <span>OR</span>
                                </div>

                                {/* UPLOAD Section */}
                                <div className="audio-input-section">
                                    <div className="section-header">
                                        <Upload size={20} className="section-icon" />
                                        <h3>Upload Audio File</h3>
                                    </div>

                                    <div
                                        className={`file-drop-zone ${dragActive ? 'active' : ''} ${audioFile && !recordedBlob ? 'has-file' : ''} ${!canClone ? 'disabled' : ''}`}
                                        onDragEnter={handleDrag}
                                        onDragOver={handleDrag}
                                        onDragLeave={handleDrag}
                                        onDrop={canClone ? handleDrop : undefined}
                                        onClick={() => canClone && document.getElementById('audio-upload').click()}
                                    >
                                        {audioFile && !recordedBlob ? (
                                            <div className="file-info">
                                                <FileAudio size={24} className="file-icon" />
                                                <span className="file-name">{audioFile.name}</span>
                                                <button
                                                    type="button"
                                                    className="file-remove"
                                                    onClick={(e) => { e.stopPropagation(); setAudioFile(null); }}
                                                >
                                                    <X size={14} />
                                                </button>
                                            </div>
                                        ) : (
                                            <>
                                                <Upload size={32} className="drop-icon" strokeWidth={1.5} />
                                                <p>Drag & drop an audio file or click to browse</p>
                                                <span className="file-types">MP3, WAV, M4A, OGG, FLAC (max 50MB)</span>
                                            </>
                                        )}
                                        <input
                                            id="audio-upload"
                                            type="file"
                                            accept="audio/*"
                                            onChange={handleFileChange}
                                            disabled={!canClone}
                                            style={{ display: 'none' }}
                                        />
                                    </div>
                                </div>

                                {/* Consent & Submit */}
                                <div className="form-group consent-group">
                                    <label className="checkbox-label">
                                        <input
                                            type="checkbox"
                                            checked={consent}
                                            onChange={(e) => setConsent(e.target.checked)}
                                            disabled={cloning || !canClone}
                                        />
                                        <span>I confirm I have the right to clone this voice and will use it responsibly.</span>
                                    </label>
                                </div>

                                <div className="form-footer-info">
                                    <p className="form-hint">Record or upload 1-3 minutes of clear audio. Costs 50 credits.</p>
                                    <div className="limit-status">
                                        <span className="limit-badge">
                                            {voiceStatus.current}/{voiceStatus.limit} voice{voiceStatus.limit !== 1 ? 's' : ''} used
                                        </span>
                                    </div>
                                </div>

                                <div className="clone-action-row">
                                    <input
                                        type="text"
                                        value={voiceName}
                                        onChange={(e) => setVoiceName(e.target.value)}
                                        placeholder="Name this voice..."
                                        maxLength={50}
                                        disabled={cloning || !canClone}
                                        className="styled-input voice-name-input-compact"
                                    />
                                    <button
                                        type="submit"
                                        className="clone-btn"
                                        disabled={cloning || !canClone || !hasAudio}
                                    >
                                        {cloning ? (
                                            <>
                                                <Loader2 size={18} className="spin" /> Cloning...
                                            </>
                                        ) : (
                                            <>
                                                <Mic size={18} />
                                                {!canClone ? 'Limit Reached' : 'Clone Voice'}
                                            </>
                                        )}
                                    </button>
                                </div>
                            </form>
                        )}
                    </div> {/* End Creator Column */}

                    {/* Right Column */}
                    <div className="voice-library-column">
                        {/* My Voices */}
                        {!needsUpgrade && (
                            <div className="voice-list-section">
                                <h2>My Cloned Voices</h2>
                                {voices.length === 0 ? (
                                    <p className="no-voices">You haven't cloned any voices yet.</p>
                                ) : (
                                    <div className="voice-list">
                                        {voices.map((voice) => (
                                            <div key={voice.id} className={`voice-card ${voice.is_default ? 'default' : ''}`}>
                                                <div className="voice-info">
                                                    <div className="voice-avatar-icon">
                                                        <Mic size={24} />
                                                    </div>
                                                    <div className="voice-details">
                                                        <h3>{voice.name}</h3>
                                                        {voice.is_default && <span className="default-badge">Default</span>}
                                                    </div>
                                                </div>
                                                <div className="voice-actions">
                                                    <button
                                                        className={`set-default-btn ${voice.is_default ? 'active' : ''}`}
                                                        onClick={() => handleSetDefault(voice.id, voice.is_default)}
                                                        title={voice.is_default ? 'Remove as default' : 'Set as default'}
                                                    >
                                                        <Star size={16} fill={voice.is_default ? "currentColor" : "none"} />
                                                    </button>
                                                    <button
                                                        className="test-voice-btn"
                                                        onClick={() => handleTestVoice(voice)}
                                                        disabled={testingVoiceId !== null}
                                                        title="Test voice"
                                                    >
                                                        {testingVoiceId === voice.id ? <Loader2 size={16} className="spin" /> : <Play size={16} />}
                                                    </button>
                                                    <button
                                                        className="delete-btn"
                                                        onClick={() => handleDelete(voice.id, voice.name)}
                                                        title="Delete voice"
                                                    >
                                                        <Trash2 size={16} />
                                                    </button>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        )}

                        {/* Tips Section */}
                        <div className="voice-tips">
                            <h3>Tips for Best Results</h3>
                            <ul>
                                <li><Mic size={14} /> Record for at least 1-3 minutes of clear audio</li>
                                <li><Volume2 size={14} /> Use a quiet environment without background noise</li>
                                <li><Play size={14} /> Speak naturally, including varied emotions and punctuation</li>
                                <li><CheckCircle2 size={14} /> Your cloned voice will appear in Rant Creator and other tools</li>
                            </ul>
                        </div>
                    </div> {/* End Library Column */}
                </div> {/* End Grid */}

            </div>
        </div>
    );
}
